import React, { FC, useState } from 'react';
import {
  Box,
  Icon,
  IconSize,
  Text,
  ToggleSwitch,
  Button,
  ButtonAppearance,
  FormControl,
  TextFieldSize,
  useToastsDispatch,
  ToastType,
  SpinnerSize,
  Spinner,
} from '@bechtel/gds';
import { get, toNumber } from 'lodash';
import { UPDATE_ROD_ROOM } from '@bechtel/client-gql';
import { ErrorModal, ModalError } from '@bechtel/client-common';
import { t, Trans } from '@lingui/macro';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import { RodRoom, UpdateRodRoomErrorCode, UpdateRodRoomInput, UpdateRodRoomPayload } from '@bechtel/types';
import { EditBarStyled, EditItemStyled, ItemStyled, TextInputStyled } from './styled';
import { getError } from './errors';
import { RodRoomsItemsTypes } from './types';

const LocationDescription: FC<{ rodRoom: RodRoom }> = ({ rodRoom }) => (
  <Box flexDirection={{ sm: 'column' }}>
    <Box mb={{ sm: '300' }}>
      <Text family="graphik" color="grey.1000" size="300">
        {rodRoom.name}
      </Text>
    </Box>
    <Text family="graphik" color="grey.600" size="300">
      {rodRoom.locationDescription}
    </Text>
  </Box>
);

const updateRodRoomToast = (rodRoomName: string) => {
  return (
    <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`The Rod Room ${rodRoomName} has been succesfully updated.`}
      </Text>
    </Box>
  );
};

export const RodRoomsItem: FC<RodRoomsItemsTypes> = ({ rodRoom, setSelectedRodRoomIds }) => {
  const [isEdit, setIsEdit] = useState(false);
  const [updateRoomError, setUpdateRodRoomError] = useState<ModalError<UpdateRodRoomErrorCode>>(null);
  const dispatchToast = useToastsDispatch();

  const [updateRodRoom, { loading }] = useMutation<
    { updateRodRoom: UpdateRodRoomPayload },
    { input: UpdateRodRoomInput }
  >(UPDATE_ROD_ROOM, {
    onCompleted: (data) => {
      const updateRodRoomError = get(data, 'updateRodRoom.error');
      if (updateRodRoomError) {
        setUpdateRodRoomError(updateRodRoomError);
      } else {
        setIsEdit(false);
        setSelectedRodRoomIds((prev) => {
          const next = new Set(prev);
          next.delete(rodRoom.id);
          return next;
        });
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => updateRodRoomToast(rodRoom.name),
            autoDismissTimeout: 5000,
          },
        });
      }
    },
    onError: (error) => {
      setUpdateRodRoomError(error);
    },
  });

  return (
    <>
      {isEdit ? (
        <EditItemStyled data-testid={`rod-room-item-edit-${rodRoom.id}`}>
          <Formik
            enableReinitialize={true}
            initialValues={{
              latitude: rodRoom.location?.latitude,
              longitude: rodRoom.location?.longitude,
              isActive: rodRoom?.isActive,
            }}
            onSubmit={(values) => {
              updateRodRoom({
                variables: {
                  input: {
                    latitude: toNumber(values.latitude),
                    longitude: toNumber(values.longitude),
                    isActive: values.isActive,
                    rodRoomId: rodRoom.id,
                  },
                },
              });
            }}
          >
            {({ setFieldValue, values, dirty, isValid, handleSubmit }) => {
              return (
                <>
                  <Box gap={{ sm: '450' }} style={{ height: '100%' }}>
                    <EditBarStyled />
                    <Box pt={{ sm: '450' }}>
                      <LocationDescription rodRoom={rodRoom} />
                    </Box>
                  </Box>
                  <Box pb={{ sm: '450' }} pt={{ sm: '550' }} gap={{ sm: '450' }}>
                    <FormControl
                      label={
                        <Text family="graphik" size="300" color="bechtel.blue.800">
                          <Trans>Latitude</Trans>
                        </Text>
                      }
                      css={`
                        flex-grow: 1;
                      `}
                    >
                      <TextInputStyled
                        size={TextFieldSize.LARGE}
                        value={values.latitude}
                        placeholder={t`Enter Latitude`}
                        onChange={(event) => {
                          setFieldValue('latitude', event.target.value);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      label={
                        <Text family="graphik" size="300" color="bechtel.blue.800">
                          <Trans>Longitude</Trans>
                        </Text>
                      }
                      css={`
                        flex-grow: 1;
                      `}
                    >
                      <TextInputStyled
                        size={TextFieldSize.LARGE}
                        value={values.longitude}
                        placeholder={t`Enter Longitude`}
                        onChange={(event) => {
                          setFieldValue('longitude', event.target.value);
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box ml={{ sm: '450' }}>
                    <ToggleSwitch
                      disabled={true}
                      isToggled={values.isActive ? true : false}
                      onToggle={() => setFieldValue('isActive', !values.isActive)}
                    />
                  </Box>
                  <Box gap={{ sm: '700' }}>
                    <Button
                      appearance={ButtonAppearance.TERTIARY}
                      disabled={loading}
                      onClick={() => {
                        setIsEdit(false);
                        setSelectedRodRoomIds((prev) => {
                          const next = new Set(prev);
                          next.delete(rodRoom.id);
                          return next;
                        });
                      }}
                    >
                      <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                        <Trans>Cancel</Trans>
                      </Text>
                    </Button>
                    <Button
                      appearance={ButtonAppearance.TERTIARY}
                      disabled={!dirty || !isValid}
                      onClick={() => handleSubmit()}
                    >
                      {loading ? (
                        <Spinner size={SpinnerSize.SMALL} />
                      ) : (
                        <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                          <Trans>Save</Trans>
                        </Text>
                      )}
                    </Button>
                  </Box>
                </>
              );
            }}
          </Formik>
        </EditItemStyled>
      ) : (
        <ItemStyled
          data-testid={`rod-room-item-${rodRoom.id}`}
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
          px={{ sm: '550' }}
          pb={{ sm: '550' }}
          pt={{ sm: '500' }}
          isActive={rodRoom.isActive ? true : false}
        >
          <LocationDescription rodRoom={rodRoom} />
          <Box>
            <Text family="graphik" color="grey.600" size="300">
              {rodRoom.location?.latitude}&#176; N, {rodRoom.location?.longitude}&#176; W
            </Text>
          </Box>
          <Icon
            icon="edit"
            size={IconSize.LARGE}
            fillColor="blue.800"
            onClick={() => {
              setIsEdit(true);
              setSelectedRodRoomIds((prev) => {
                return new Set(prev).add(rodRoom.id);
              });
            }}
            style={{ cursor: 'pointer' }}
          />
        </ItemStyled>
      )}
      {updateRoomError && (
        <ErrorModal
          title={t`Update Rod Room Error`}
          error={getError(updateRoomError)}
          close={() => setUpdateRodRoomError(null)}
        />
      )}
    </>
  );
};
